/**
 * Shared modal dialog. Centered card over a dimmed backdrop.
 *
 * - Closes on Escape and on backdrop click.
 * - Locks body scroll while open.
 * - Rendered via a portal so it escapes any transformed/overflow-hidden ancestors.
 * - Accessible: role=dialog + aria-modal + labelled by the title.
 */

import { useEffect } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

// Stack of open modals (by identity) so Escape only closes the topmost one when modals nest
// (e.g. an attachment preview opened from inside a ticket modal).
const modalStack: object[] = [];

export function Modal({
  open,
  title,
  onClose,
  children,
  size = "md",
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: "md" | "lg";
}) {
  useEffect(() => {
    if (!open) return;
    const token = {};
    modalStack.push(token);
    const onKey = (e: KeyboardEvent) => {
      // Only the topmost modal reacts to Escape, so nested modals close one at a time.
      if (e.key === "Escape" && modalStack[modalStack.length - 1] === token) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      const idx = modalStack.indexOf(token);
      if (idx !== -1) modalStack.splice(idx, 1);
      // Only restore scroll when no modals remain open.
      if (modalStack.length === 0) document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const maxW = size === "lg" ? "max-w-2xl" : "max-w-lg";

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-950/45 p-4 backdrop-blur-[2px] sm:items-center"
      onMouseDown={(e) => {
        // close only when the backdrop itself (not the card) is pressed
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`animate-fade-in w-full ${maxW} overflow-hidden rounded-2xl border border-line bg-surface shadow-raised`}
      >
        <header className="flex items-start justify-between gap-3 border-b border-line bg-raised px-5 py-3.5">
          {/* A long title (an attachment's filename) wraps beside the close button instead of
              pushing it out of the dialog: two lines at most, the full text in the tooltip. The
              margin, not padding, centres one line on the button (padding would show a sliver of
              a clamped third line) (RESP-06). */}
          <h3 title={title} className="my-1 line-clamp-2 min-w-0 flex-1 font-semibold text-brand-900 wrap-anywhere">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-muted transition hover:bg-brand-50 hover:text-brand"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
        {/* dvh: the height actually visible, so the body never runs under a phone's URL bar.
            On a short (landscape) screen it gives up the 80% so the header still fits too (RESP-07). */}
        <div className="max-h-[min(80dvh,calc(100dvh-6rem))] overflow-y-auto p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
